import {
  useCookie,
  useCookieUpdateCallback,
  useCookieUpdateEffect,
  useStaleCookieValue,
} from '@aboutyou/storage';
import {useCallback, useMemo, useState} from 'react';
import {useMergedFeatureFlagConfig} from '../Provider';
import {AvailableFeatureNames, Experiment} from '../types';
import {abTestsActivatedBeforeCookieConsent} from './abTestsActivatedBeforeCookieConsent';
import {getAbTestGroups} from './getAbTestExperiments';
import {ABTests, AB_TEST_COOKIES} from './types';

const extractAbTestsGroupsFromCookie = (cookieValue?: string) => {
  if (!cookieValue) {
    return [];
  }

  const abTestsGroup = getAbTestGroups(cookieValue) as ABTests;

  // filter out already activated abTests before cookie consent since those experiments should never be considered or activated for this user in this session
  const filteredAbTestsGroup = abTestsGroup.filter(abTest => {
    if (abTestsActivatedBeforeCookieConsent.has(abTest.key)) {
      return false;
    }
    return true;
  });

  return filteredAbTestsGroup;
};

const useExtractAbTestsGroupsFromCookie = (cookieValue?: string) => {
  return useMemo(() => {
    return extractAbTestsGroupsFromCookie(cookieValue);
  }, [cookieValue]);
};

export const useExtractAbTestsGroupsFromCookieCallback = () => {
  return useCallback((cookieValue?: string) => {
    return extractAbTestsGroupsFromCookie(cookieValue);
  }, []);
};

/**
 * use this hook to get the most recent abTests cookie value.
 * This hooks will cause rerender on cookie update after the user accepts Cookie Consent
 */
export const useAbTestExperimentsCookie = (): ABTests => {
  const [cookieValue] = useCookie({
    cookieName: AB_TEST_COOKIES.Experiments,
    defaultValue: undefined,
    transformRawValue: rawValue => rawValue,
  });

  return useExtractAbTestsGroupsFromCookie(cookieValue);
};

/**
 * This hooks gets the stale abTests Cookie value.
 * Any updates to 'ay-active-ab-tests' cookie will be ignored to avoid refetching of the request.
 */
export const useStaleAbTestExperimentsCookie = (): ABTests => {
  const cookieValueStale = useStaleCookieValue({
    cookieName: AB_TEST_COOKIES.Experiments,
    defaultValue: undefined,
    transformRawValue: rawValue => rawValue,
  });

  return useExtractAbTestsGroupsFromCookie(cookieValueStale);
};

/**
 * This hooks is optimized for rendering based on cookie update.
 * It gets the stale abTests Cookie value and updates the cookie value only if the current feature is enabled.
 */
export const useAbTestExperimentsCookieByFeatureFlag = ({
  featureName,
}: {
  featureName: AvailableFeatureNames;
}): ABTests => {
  const cookieValueStale = useStaleCookieValue({
    cookieName: AB_TEST_COOKIES.Experiments,
    defaultValue: undefined,
    transformRawValue: rawValue => rawValue,
  });
  const mergedFeatureFlagConfig = useMergedFeatureFlagConfig();
  const currentConfig = mergedFeatureFlagConfig[featureName];

  const [cookieValue, setCookieValue] = useState(cookieValueStale);

  const handleCookieUpdate = useCallback(
    (cookieUpdatedValue: string | undefined) => {
      if (!cookieUpdatedValue || !currentConfig?.experiment) {
        return;
      }
      if (cookieUpdatedValue === cookieValue) {
        return;
      }

      const abTestsGroup = getAbTestGroups(cookieUpdatedValue);
      // filter out already activated abTests before cookie consent since those experiments should never be considered or activated for this user in this session
      const filteredAbTestsGroup = abTestsGroup.filter(abTest => {
        if (abTestsActivatedBeforeCookieConsent.has(abTest.key)) {
          return false;
        }
        return true;
      });

      const experimentsInCurrentConfig = (
        Array.isArray(currentConfig.experiment)
          ? currentConfig.experiment
          : [currentConfig.experiment]
      ) as Experiment[];
      const experimentKeys = experimentsInCurrentConfig.map(
        experiment => experiment.name,
      );

      if (
        experimentKeys.some(experimentKey =>
          filteredAbTestsGroup.some(abTest => abTest.key === experimentKey),
        )
      ) {
        setCookieValue(cookieUpdatedValue);
      }
    },
    [cookieValue, currentConfig?.experiment],
  );

  useCookieUpdateEffect<string>(
    AB_TEST_COOKIES.Experiments,
    handleCookieUpdate,
  );

  return useExtractAbTestsGroupsFromCookie(cookieValue);
};

export const useAbTestExperimentsCookieUpdate = () => {
  return useCookieUpdateCallback({
    cookieName: AB_TEST_COOKIES.Experiments,
    config: {
      expirationInDays: 60,
    },
  });
};
